import { type Component, type JSX, createResource, createSignal, For, Show, createMemo } from 'solid-js'
import { Trans, useTransContext } from '@mbarzda/solid-i18next'
import { useCorisModeration } from '../../../shared/providers/coris/coris-moderation.provider'
import { createSSE } from '../../../shared/primitives/createSSE'
import type { ModerationActionType, ModerationMessage, ModerationFilterParams } from '../domain/models/moderation.model'
import ModerationMessageCard, { SkeletonModerationCard } from './ModerationMessageCard'
import PeriodSelector from '../../../components/shared/components/PeriodSelector'
import {
  type SimplePeriodType,
  type CustomDateRange,
  getDateRangesForSimplePeriod
} from '../../../shared/helpers/date.functions'
import { Icon } from '@iconify-icon/solid'

type Tab = 'inbox' | 'reviewed'
type SortOrder = 'newest' | 'oldest'

interface DropdownOption<T extends string = string> {
  value: T
  label: string
  render?: () => JSX.Element
}

function FilterDropdown<T extends string>(props: {
  options: DropdownOption<T>[]
  value: T
  onChange: (value: T) => void
}): JSX.Element {
  const [isOpen, setIsOpen] = createSignal(false)

  const selectedLabel = () => props.options.find((o) => o.value === props.value)?.label ?? props.value
  const selectedRender = () => props.options.find((o) => o.value === props.value)?.render

  return (
    <div class="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen())}
        class="inline-grid grid-cols-[1fr_auto] items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 max-w-[14rem]"
      >
        {/* Invisible sizer: renders all options (including avatars) to establish min-width, clamped to h-5 */}
        <span
          class="invisible pointer-events-none grid [grid-area:1/1] whitespace-nowrap h-5 overflow-hidden"
          aria-hidden="true"
        >
          <For each={props.options}>
            {(option) => (
              <span class="[grid-area:1/1]">
                <Show when={option.render} fallback={option.label}>
                  {option.render!()}
                </Show>
              </span>
            )}
          </For>
        </span>
        {/* Visible selected value — clamped to h-5 so custom renders (avatars) don't stretch the button */}
        <span class="[grid-area:1/1] h-5 flex items-center text-left overflow-hidden">
          <Show when={selectedRender()} fallback={<span class="truncate">{selectedLabel()}</span>}>
            {selectedRender()!()}
          </Show>
        </span>
        <Icon
          icon="heroicons:chevron-down"
          class="text-[1rem] text-gray-400 transition-transform duration-200"
          classList={{ 'rotate-180': isOpen() }}
        />
      </button>

      <Show when={isOpen()}>
        <div class="absolute left-0 z-20 mt-2 min-w-full w-max rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div class="py-1">
            <For each={props.options}>
              {(option) => (
                <button
                  type="button"
                  onClick={() => {
                    props.onChange(option.value)
                    setIsOpen(false)
                  }}
                  class="w-full max-w-[14rem] px-4 py-2 text-left text-sm transition-colors hover:bg-gray-100 flex items-center gap-2 truncate"
                  classList={{
                    'bg-gray-100 text-gray-900 font-medium': option.value === props.value,
                    'text-gray-700': option.value !== props.value
                  }}
                >
                  <Show when={option.render} fallback={option.label}>
                    {option.render!()}
                  </Show>
                </button>
              )}
            </For>
          </div>
        </div>
      </Show>

      <Show when={isOpen()}>
        <div class="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
      </Show>
    </div>
  )
}

const PAGE_SIZE = 25

const ModerationPage: Component = () => {
  const [t] = useTransContext()
  const { getInboxMessages, getReviewedMessages, applyAction, getFlaggingRules, getSSEOptions } = useCorisModeration()
  const [activeTab, setActiveTab] = createSignal<Tab>('inbox')

  // Sort & Filter state
  const [sortOrder, setSortOrder] = createSignal<SortOrder>('newest')
  const [filterKeywordList, setFilterKeywordList] = createSignal('all')
  const [filterPeriod, setFilterPeriod] = createSignal<SimplePeriodType>('all_time')
  const [filterCustomRange, setFilterCustomRange] = createSignal<CustomDateRange | undefined>()
  const [currentPage, setCurrentPage] = createSignal(1)

  // Flagging rules resource
  const [rules] = createResource(async () => await getFlaggingRules.execute())

  const keywordOptions = (): DropdownOption[] => [
    { value: 'all', label: t('ls_moderation_filter_all_lists') },
    ...(rules() ?? []).map((rule) => ({
      value: String(rule.id),
      label: rule.name
    }))
  ]

  const dateRange = () => {
    const period = filterPeriod()
    if (period === 'all_time') return null
    if (period === 'custom') {
      const custom = filterCustomRange()
      if (!custom) return null
      return { from: custom.from, to: custom.to }
    }
    const ranges = getDateRangesForSimplePeriod(period)
    return { from: new Date(ranges.fromDate), to: new Date(ranges.toDate) }
  }

  const filterParams = (): Omit<ModerationFilterParams, 'needsAction'> => {
    const params: Omit<ModerationFilterParams, 'needsAction'> = {
      page: currentPage(),
      pageSize: PAGE_SIZE
    }
    const kw = filterKeywordList()
    if (kw !== 'all') params.flaggingRuleId = Number(kw)
    const range = dateRange()
    if (range) {
      params.dateFrom = range.from.toISOString()
      params.dateTo = range.to.toISOString()
    }
    return params
  }

  // Memoize filter params to avoid re-creating objects on every render
  const memoizedFilterParams = createMemo(() => filterParams(), undefined, {
    equals: (a, b) => JSON.stringify(a) === JSON.stringify(b)
  })

  const [inboxData, { refetch: refetchInbox }] = createResource(
    () => (activeTab() === 'inbox' ? memoizedFilterParams() : false),
    async (params) => await getInboxMessages.execute(params as Omit<ModerationFilterParams, 'needsAction'>)
  )

  const [reviewedData, { refetch: refetchReviewed }] = createResource(
    () => (activeTab() === 'reviewed' ? memoizedFilterParams() : false),
    async (params) => await getReviewedMessages.execute(params as Omit<ModerationFilterParams, 'needsAction'>)
  )

  // SSE: refetch when backend notifies of changes
  getSSEOptions(() => {
    refetchInbox()
    refetchReviewed()
  }).then((opts) => createSSE(opts))

  const sortItems = (items: ModerationMessage[]): ModerationMessage[] => {
    return [...items].sort((a, b) =>
      sortOrder() === 'newest' ? b.date.getTime() - a.date.getTime() : a.date.getTime() - b.date.getTime()
    )
  }

  const handleAction = async (messageId: string, action: ModerationActionType): Promise<void> => {
    try {
      await applyAction.execute(messageId, action)
      refetchInbox()
      refetchReviewed()
    } catch (error) {
      console.error('Moderation action failed:', error)
    }
  }

  const clearFilters = (): void => {
    setFilterKeywordList('all')
    setFilterPeriod('all_time')
    setFilterCustomRange(undefined)
    setCurrentPage(1)
  }

  const paginationInfo = () => {
    const data = activeTab() === 'inbox' ? inboxData() : reviewedData()
    return data?.pagination ?? null
  }

  const totalPages = () => {
    const info = paginationInfo()
    if (!info) return 1
    return Math.max(1, Math.ceil(info.total / info.pageSize))
  }

  return (
    <div class="flex flex-col gap-0 h-full">
      {/* Tab bar */}
      <div class="p-6 pb-0 border-b border-gray-200">
        <div class="max-w-[1280px] mx-auto w-full border-b border-gray-200">
          <div class="flex items-center gap-6">
            <button
              type="button"
              class="pb-4 text-sm font-medium border-b-2 -mb-px"
              classList={{
                'border-primary-600 text-primary-600': activeTab() === 'inbox',
                'border-transparent text-gray-500 hover:text-gray-700': activeTab() !== 'inbox'
              }}
              onClick={() => setActiveTab('inbox')}
            >
              <Trans key="ls_moderation_tab_inbox" />
            </button>
            <button
              type="button"
              class="pb-4 text-sm font-medium border-b-2 -mb-px"
              classList={{
                'border-primary-600 text-primary-600': activeTab() === 'reviewed',
                'border-transparent text-gray-500 hover:text-gray-700': activeTab() !== 'reviewed'
              }}
              onClick={() => setActiveTab('reviewed')}
            >
              <Trans key="ls_moderation_tab_reviewed" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div class="p-6 pb-16 flex flex-col gap-6">
        <div class="max-w-[1280px] mx-auto w-full flex flex-col gap-4">
          {/* Toolbar: Filters left, Sort right */}
          <div class="flex items-center gap-3">
            <FilterDropdown options={keywordOptions()} value={filterKeywordList()} onChange={setFilterKeywordList} />

            <PeriodSelector
              selectedPeriod={filterPeriod()}
              onPeriodChange={setFilterPeriod}
              customDateRange={filterCustomRange()}
              onCustomRangeChange={(from, to) => setFilterCustomRange({ from, to })}
              showCompareSelector={false}
            />

            <button
              type="button"
              onClick={clearFilters}
              class="shrink-0 px-4 py-2 bg-white rounded-lg border border-gray-300 shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {t('ls_moderation_clear_filters')}
            </button>

            <div class="flex-1" />

            <span class="text-sm text-gray-500">{t('ls_moderation_sorting_label')}</span>
            <button
              type="button"
              onClick={() => setSortOrder((prev) => (prev === 'newest' ? 'oldest' : 'newest'))}
              class="inline-flex items-center gap-1.5 px-4 py-2 bg-white rounded-lg border border-gray-300 shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Icon
                icon={sortOrder() === 'newest' ? 'mdi:sort-clock-descending' : 'mdi:sort-clock-ascending'}
                width={16}
                height={16}
              />
              {sortOrder() === 'newest' ? t('ls_moderation_sort_newest') : t('ls_moderation_sort_oldest')}
            </button>
          </div>

          {/* Message list */}
          <div class="bg-white rounded-xl ring-1 ring-gray-200 flex flex-col overflow-hidden">
            <Show when={activeTab() === 'inbox'}>
              <Show when={inboxData.loading && !inboxData()}>
                <For each={[1, 2, 3]}>{() => <SkeletonModerationCard />}</For>
              </Show>
              <For
                each={sortItems(inboxData()?.items ?? [])}
                fallback={
                  <Show when={!inboxData.loading}>
                    <div class="p-6 text-gray-500">{t('ls_moderation_empty_inbox')}</div>
                  </Show>
                }
              >
                {(message) => <ModerationMessageCard message={message} showActions={true} onAction={handleAction} />}
              </For>
            </Show>

            <Show when={activeTab() === 'reviewed'}>
              <Show when={reviewedData.loading && !reviewedData()}>
                <For each={[1, 2, 3]}>{() => <SkeletonModerationCard />}</For>
              </Show>
              <For
                each={sortItems(reviewedData()?.items ?? [])}
                fallback={
                  <Show when={!reviewedData.loading}>
                    <div class="p-6 text-gray-500">{t('ls_moderation_empty_reviewed')}</div>
                  </Show>
                }
              >
                {(message) => <ModerationMessageCard message={message} showActions={false} />}
              </For>
            </Show>
          </div>

          <Show when={totalPages() > 1}>
            <div class="flex items-center justify-center gap-2 mt-4">
              <button
                type="button"
                disabled={currentPage() <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                class="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('ls_moderation_prev_page')}
              </button>
              <span class="text-sm text-gray-600">
                {currentPage()} / {totalPages()}
              </span>
              <button
                type="button"
                disabled={currentPage() >= totalPages()}
                onClick={() => setCurrentPage((p) => p + 1)}
                class="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('ls_moderation_next_page')}
              </button>
            </div>
          </Show>
        </div>
      </div>
    </div>
  )
}

export default ModerationPage
